import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

// Style layers, in order: tokens -> base -> primitives -> layout.
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/layout.css';
import './styles/storefront.css';
import './styles/cart.css';
import './styles/checkout.css';
import './styles/admin.css';
import './styles/chatbot.css';

import { ToastProvider } from './components/ui/Toast';
import { SystemProvider } from './context/SystemContext';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { AuthUIProvider } from './context/AuthUIContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/route/ProtectedRoute';
import AdminRoute from './components/route/AdminRoute';

import Home from './pages/Home';
import ProductList from './pages/ProductList';
import ProductDetail from './pages/ProductDetail';
import CategoryPage from './pages/CategoryPage';
import CartPage from './pages/CartPage';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import Orders from './pages/Orders';
import Profile from './pages/Profile';
import ChangePassword from './pages/ChangePassword';
import OrderDetail from './pages/OrderDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Health from './pages/Health';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import NotFound from './pages/NotFound';

import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/Admin';
import AdminProducts from './pages/admin/Products';
import ProductEditor from './pages/admin/ProductEditor';
import AdminCategories from './pages/admin/Categories';
import CategoryEditor from './pages/admin/CategoryEditor';
import AdminUsers from './pages/admin/Users';
import UserEditor from './pages/admin/UserEditor';
import AdminShip from './pages/admin/Ship';
import AdminPrint from './pages/admin/Print';
import AdminPickups from './pages/admin/Pickups';
import AdminDeletedOrders from './pages/admin/DeletedOrders';
import AdminChatConsole from './pages/admin/ChatConsole';
import AdminChatInteract from './pages/admin/ChatInteract';

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <SystemProvider>
          <AuthProvider>
            <CartProvider>
              <AuthUIProvider>
                <Routes>
                  <Route element={<Layout />}>
                    {/* public */}
                    <Route index element={<Home />} />
                    <Route path="products" element={<ProductList />} />
                    <Route path="products/:id" element={<ProductDetail />} />
                    <Route path="category/*" element={<CategoryPage />} />
                    <Route path="cart" element={<CartPage />} />
                    <Route path="checkout" element={<Checkout />} />
                    <Route path="about" element={<About />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="terms-of-use" element={<TermsOfUse />} />
                    <Route path="health" element={<Health />} />
                    <Route path="reset-password" element={<ResetPassword />} />
                    <Route path="verify-email" element={<VerifyEmail />} />

                    {/* authed */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="order/:orderNumber" element={<OrderConfirmation />} />
                      <Route path="orders" element={<Orders />} />
                      <Route path="orders/:orderNumber" element={<OrderDetail />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="profile/password" element={<ChangePassword />} />
                    </Route>

                    {/* admin */}
                    <Route element={<AdminRoute />}>
                      <Route path="admin" element={<AdminLayout />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="products" element={<AdminProducts />} />
                        <Route path="products/new" element={<ProductEditor />} />
                        <Route path="products/:id" element={<ProductEditor />} />
                        <Route path="categories" element={<AdminCategories />} />
                        <Route path="categories/new" element={<CategoryEditor />} />
                        <Route path="categories/:id" element={<CategoryEditor />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="users/:id" element={<UserEditor />} />
                        <Route path="ship" element={<AdminShip />} />
                        <Route path="pickups" element={<AdminPickups />} />
                        <Route path="deleted-orders" element={<AdminDeletedOrders />} />
                        <Route path="chat" element={<AdminChatConsole />} />
                        <Route path="chat/:id" element={<AdminChatInteract />} />
                        <Route path="print" element={<AdminPrint />} />
                      </Route>
                    </Route>

                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              </AuthUIProvider>
            </CartProvider>
          </AuthProvider>
        </SystemProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
